import * as React from 'react';
import { Users } from 'lucide-react';
import { Input } from '@/components/forms/Input';
import { Select } from '@/components/forms/Select';
import { DatePicker } from '@/components/forms/DatePicker';
import {
  BOOLEAN_SELECT_OPTIONS,
  ENGAGEMENT_FIELD_LABELS,
} from '@/components/establishments/establishment-configs';
import { addDaysToDateString } from '@/utils/format-date-utils';
import type {
  EstablishmentFormFieldValidation,
  EstablishmentFormMode,
  EstablishmentFormState,
} from '@/types/establishment.types';

/** Props for {@link EstablishmentEngagementSection}. */
interface Props {
  mode: EstablishmentFormMode;
  form: EstablishmentFormState;
  onFieldChange: (field: string, value: unknown) => void;
  onBlur: (field: string) => void;
  validation: EstablishmentFormFieldValidation;
}

/** Engagement dates, hours, and related establishment fields. */
export function EstablishmentEngagementSection({
  mode,
  form,
  onFieldChange,
  onBlur,
  validation,
}: Readonly<Props>) {
  function handleVolunteerQuotaChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onFieldChange('volunteerQuota', e.target.value);
  }
  function handleVolunteerQuotaBlur(): void {
    onBlur('volunteerQuota');
  }
  function handleStudentQuotaChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onFieldChange('studentQuota', e.target.value);
  }
  function handleStudentQuotaBlur(): void {
    onBlur('studentQuota');
  }
  function handleVolunteersToRecruitChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onFieldChange('volunteersToRecruit', e.target.value);
  }
  function handleVolunteersToRecruitBlur(): void {
    onBlur('volunteersToRecruit');
  }
  function handleMaxPastDaysChange(e: React.ChangeEvent<HTMLInputElement>): void {
    onFieldChange('maxPastDays', e.target.value);
  }
  function handleMaxPastDaysBlur(): void {
    onBlur('maxPastDays');
  }
  function handleStartDateChange(value: string): void {
    onFieldChange('engagementStartDate', value);
  }
  function handleStartDateBlur(): void {
    onBlur('engagementStartDate');
  }
  function handleEndDateChange(value: string): void {
    onFieldChange('engagementEndDate', value);
  }
  function handleEndDateBlur(): void {
    onBlur('engagementEndDate');
  }
  function handleIsQpvChange(value: string): void {
    onFieldChange('isQpv', value);
  }
  function handleRenewalChange(value: string): void {
    onFieldChange('renewalNextYear', value);
  }

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <Users className="w-4 h-4" />
        Engagement & Contrat
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label={ENGAGEMENT_FIELD_LABELS.vscToRecruit}
          type="number"
          value={form.volunteerQuota}
          onChange={handleVolunteerQuotaChange}
          onBlur={handleVolunteerQuotaBlur}
          placeholder="Ex: 50"
          min={0}
          state={validation.getFieldState('volunteerQuota')}
          error={validation.getFieldError('volunteerQuota')}
          inputSize="sm"
        />
        <Input
          label={ENGAGEMENT_FIELD_LABELS.studentsToAccompany}
          type="number"
          value={form.studentQuota}
          onChange={handleStudentQuotaChange}
          onBlur={handleStudentQuotaBlur}
          placeholder="Ex: 120"
          min={0}
          state={validation.getFieldState('studentQuota')}
          error={validation.getFieldError('studentQuota')}
          inputSize="sm"
        />
        <Input
          label={ENGAGEMENT_FIELD_LABELS.volunteersToRecruit}
          type="number"
          value={form.volunteersToRecruit}
          onChange={handleVolunteersToRecruitChange}
          onBlur={handleVolunteersToRecruitBlur}
          placeholder="Ex: 10"
          min={0}
          state={validation.getFieldState('volunteersToRecruit')}
          error={validation.getFieldError('volunteersToRecruit')}
          inputSize="sm"
        />
        {renderVolunteersPerEstablishment(mode)}
        <Input
          label={ENGAGEMENT_FIELD_LABELS.maxPastDays}
          labelTooltip="Nombre maximum de jours dans le passé autorisés pour créer ou modifier une séance dans cet établissement."
          type="number"
          value={form.maxPastDays}
          onChange={handleMaxPastDaysChange}
          onBlur={handleMaxPastDaysBlur}
          placeholder="Ex: 7"
          min={0}
          max={7}
          state={validation.getFieldState('maxPastDays')}
          error={validation.getFieldError('maxPastDays')}
          inputSize="sm"
        />
        <DatePicker
          label="Date de début partenariat"
          value={form.engagementStartDate}
          onChange={handleStartDateChange}
          onBlur={handleStartDateBlur}
          placeholder="Sélectionner la date de début"
          state={validation.getFieldState('engagementStartDate')}
          error={validation.getFieldError('engagementStartDate')}
          inputSize="sm"
        />
        <DatePicker
          label="Date fin partenariat"
          value={form.engagementEndDate}
          onChange={handleEndDateChange}
          onBlur={handleEndDateBlur}
          placeholder="Sélectionner la date de fin"
          minDate={addDaysToDateString(form.engagementStartDate, 1)}
          state={validation.getFieldState('engagementEndDate')}
          error={validation.getFieldError('engagementEndDate')}
          inputSize="sm"
        />
        <Select
          label="Quartier prioritaire de la ville (QPV)"
          value={form.isQpv}
          onChange={handleIsQpvChange}
          options={BOOLEAN_SELECT_OPTIONS}
          placeholder="Non renseigné"
          size="sm"
        />
        <Select
          label="Reconduction prévue N+1"
          value={form.renewalNextYear}
          onChange={handleRenewalChange}
          options={BOOLEAN_SELECT_OPTIONS}
          placeholder="Non renseigné"
          size="sm"
        />
      </div>
      <p className="text-xs text-muted-foreground mt-2">
        Définissez le nombre maximum d'élèves et de Volontaires pour cet établissement
      </p>
    </div>
  );
}

/** Read-only volunteers-per-establishment field, shown only on create. */
function renderVolunteersPerEstablishment(mode: EstablishmentFormMode): React.ReactNode {
  if (mode !== 'create') return null;
  return (
    <Input
      label={ENGAGEMENT_FIELD_LABELS.volunteersPerEstablishment}
      type="number"
      value={0}
      readOnly
      disabled
      placeholder="—"
      inputSize="sm"
    />
  );
}
